import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  PasButtonRoundColor,
  PasButtonRoundComponent,
  PasConfirmationService,
} from '@porsche-aftersales/frontend-toolbox';
import { ICellRendererAngularComp } from 'ag-grid-angular';

import { isEditable, isViewable } from '../../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { BookingRoutingService } from '../../../services/details-view-service/booking-routing.service';
import { UserService } from '../../../services/user-service/user.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'recourse-booking-button-cell-renderer',
  templateUrl: './recourse-booking-button-cell-renderer.component.html',
  imports: [PasButtonRoundComponent],
})
export class RecourseBookingButtonCellRenderer implements ICellRendererAngularComp {
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly userService = inject(UserService);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly translateService = inject(TranslateService);
  private readonly pasConfirmationService = inject(PasConfirmationService);

  public params: any;
  private editable = true;

  // Receiving data from the selected row
  public agInit(params: any): void {
    this.params = params;
    this.editable = isEditable(this.params.data, this.userService.getUser(), true);
  }

  public onDetailClicked(): void {
    if (
      this.params.context.parentReferenceBookingsComponent &&
      this.params.context.parentReferenceBookingsComponent.getParentFormChanged()
    ) {
      // when clicking on Detail Button on reference-bookings overview row and data from general data have been changed
      this.confirmationDialog();
    } else {
      this.openEditDetailsView();
    }
  }

  // componentParent is always define in this case
  // because this method is only called by clicking Edit button on recourse-booking.component.ts
  public onEditClicked(): void {
    this.bookingRoutingService.openBookingDetailsEdit(this.params.data.pkBookingId);
  }

  private openEditDetailsView(): void {
    this.bookingRoutingService.openBookingDetailsView(this.params.data.pkBookingId);
  }

  /*
   * If table is shown on edit/details tab, then it will not have the component parent inside the table context
   * because it's a table of referenced bookings
   */
  private isReferenceBookingsTable(): boolean {
    return !(this.params.context && this.params.context.componentParent);
  }

  public isEditable(): boolean {
    return this.editable && !this.isReferenceBookingsTable();
  }

  private isBookingOpenedInView(): boolean {
    return (
      this.isReferenceBookingsTable() &&
      this.params.data.pkBookingId === Number(this.recourseBookingStore.selectedRecourseBookingId)
    );
  }

  public isDetailsViewAvailable(): boolean {
    return isViewable(this.params.data, this.userService.getUser()) && !this.isBookingOpenedInView();
  }

  public refresh(): boolean {
    return false;
  }

  // Confirmation dialog for reference overview Detail btn, we are staying on the same url="/recourseBooking"
  public confirmationDialog(): boolean {
    this.translateService
      .get(['FORM_CONTAINS_UNSAVED_DATA_TITLE', 'FORM_CONTAINS_UNSAVED_DATA_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.FORM_CONTAINS_UNSAVED_DATA_TITLE;
        const message: string = translate.FORM_CONTAINS_UNSAVED_DATA_MESSAGE;
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, false).then(
          () => {
            this.openEditDetailsView();
          },
          () => {
            // stay where you are and do nothing, close information dialog
          },
        );
      });
    return false;
  }

  public readonly pasButtonRoundColor = PasButtonRoundColor;
}
